import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  Building2,
  Users,
  Sparkles,
  Palette,
  ChevronRight,
  ChevronLeft,
  Check,
  ArrowRight,
  X,
  Rocket,
  Target
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  { id: 'welcome', title: 'Welcome to RaiseRoot', icon: Rocket },
  { id: 'organization', title: 'Your Organization', icon: Building2 },
  { id: 'first-partner', title: 'Add a Partner', icon: Users },
  { id: 'generate', title: 'AI Magic', icon: Sparkles },
  { id: 'complete', title: 'You\'re Ready!', icon: Check },
];

export default function QuickStartWizard({ onClose, onComplete }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [orgData, setOrgData] = useState({
    name: user?.organization_name || '',
    mission: '',
    programs: '',
  });
  const [partnerData, setPartnerData] = useState({
    name: '',
    website_url: '',
    industry: '',
  });

  const handleOrgChange = useCallback((field, value) => {
    setOrgData(prev => ({ ...prev, [field]: value }));
  }, []);

  const handlePartnerChange = useCallback((field, value) => {
    setPartnerData(prev => ({ ...prev, [field]: value }));
  }, []);

  const saveOrganization = async () => {
    if (!orgData.name.trim()) {
      toast.error('Organization name is required');
      return false;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('pitchpal_token');
      const res = await fetch(`${API}/api/organization`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(orgData),
      });
      if (res.ok) {
        toast.success('Organization saved!');
        return true;
      }
      return false;
    } catch {
      toast.error('Failed to save organization');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const savePartner = async () => {
    if (!partnerData.name.trim()) {
      return true; // Skip if empty
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('pitchpal_token');
      const res = await fetch(`${API}/api/partners`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(partnerData),
      });
      if (res.ok) {
        toast.success('Partner added!');
        return true;
      }
      return false;
    } catch {
      toast.error('Failed to add partner');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleNext = async () => {
    if (currentStep === 1) {
      const success = await saveOrganization();
      if (!success) return;
    }
    if (currentStep === 2) {
      const success = await savePartner();
      if (!success) return;
    }
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleFinish = () => {
    localStorage.setItem('raiseroot_onboarded', 'true');
    if (onComplete) onComplete();
    if (onClose) onClose();
  };

  const progress = ((currentStep) / (STEPS.length - 1)) * 100;
  const StepIcon = STEPS[currentStep].icon;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="quick-start-wizard">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden animate-in fade-in zoom-in-95 duration-300">
        {/* Progress Bar */}
        <div className="h-1.5 bg-stone-100">
          <div
            className="h-full bg-primary transition-all duration-500 ease-out rounded-r-full"
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-5 pb-2">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary-50 rounded-xl flex items-center justify-center">
              <StepIcon className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-xs text-stone-400 uppercase tracking-wider font-medium">
                Step {currentStep + 1} of {STEPS.length}
              </p>
              <h3 className="font-heading text-lg font-bold text-stone-900">
                {STEPS[currentStep].title}
              </h3>
            </div>
          </div>
          <button
            onClick={handleFinish}
            className="text-stone-400 hover:text-stone-600 transition-colors p-1"
            data-testid="wizard-close-btn"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-5 min-h-[280px]">
          {currentStep === 0 && (
            <div className="text-center py-4" data-testid="wizard-step-welcome">
              <div className="w-16 h-16 bg-primary-100 rounded-2xl mx-auto flex items-center justify-center mb-4">
                <Rocket className="w-8 h-8 text-primary" />
              </div>
              <h2 className="font-heading text-xl font-bold text-stone-900 mb-3">
                Let's get you started in under 5 minutes
              </h2>
              <p className="text-stone-600 text-sm leading-relaxed max-w-sm mx-auto">
                We'll walk you through setting up your organization, adding your first partner, 
                and generating your first funding proposal.
              </p>
              <div className="flex flex-col gap-2 mt-6 text-left max-w-xs mx-auto">
                {['Set up your organization', 'Add a corporate partner', 'Generate your first proposal', 'Explore Design Studio'].map((item, i) => (
                  <div key={i} className="flex items-center gap-3 text-sm text-stone-600">
                    <div className="w-6 h-6 bg-primary-50 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-xs font-bold text-primary">{i + 1}</span>
                    </div>
                    {item}
                  </div>
                ))}
              </div>
            </div>
          )}

          {currentStep === 1 && (
            <div className="space-y-4" data-testid="wizard-step-organization">
              <p className="text-sm text-stone-600 mb-4">
                Tell us about your nonprofit so our AI can tailor proposals to your mission.
              </p>
              <div>
                <Label className="text-stone-700">Organization Name *</Label>
                <Input
                  value={orgData.name}
                  onChange={(e) => handleOrgChange('name', e.target.value)}
                  placeholder="e.g., Helping Hands Foundation"
                  data-testid="wizard-org-name"
                />
              </div>
              <div>
                <Label className="text-stone-700">Mission Statement</Label>
                <Textarea
                  value={orgData.mission}
                  onChange={(e) => handleOrgChange('mission', e.target.value)}
                  placeholder="What does your organization do? Who do you serve?"
                  rows={3}
                  data-testid="wizard-org-mission"
                />
              </div>
              <div>
                <Label className="text-stone-700">Key Programs</Label>
                <Input
                  value={orgData.programs}
                  onChange={(e) => handleOrgChange('programs', e.target.value)}
                  placeholder="e.g., Youth Mentoring, Food Bank, Education"
                  data-testid="wizard-org-programs"
                />
              </div>
            </div>
          )}

          {currentStep === 2 && (
            <div className="space-y-4" data-testid="wizard-step-partner">
              <p className="text-sm text-stone-600 mb-4">
                Add a potential corporate sponsor. You can skip this and add partners later.
              </p>
              <div>
                <Label className="text-stone-700">Company Name</Label>
                <Input
                  value={partnerData.name}
                  onChange={(e) => handlePartnerChange('name', e.target.value)}
                  placeholder="e.g., Nike, Google, Local Business"
                  data-testid="wizard-partner-name"
                />
              </div>
              <div>
                <Label className="text-stone-700">Website URL</Label>
                <Input
                  value={partnerData.website_url}
                  onChange={(e) => handlePartnerChange('website_url', e.target.value)}
                  placeholder="https://www.company.com"
                  data-testid="wizard-partner-url"
                />
              </div>
              <div>
                <Label className="text-stone-700">Industry</Label>
                <Input
                  value={partnerData.industry}
                  onChange={(e) => handlePartnerChange('industry', e.target.value)}
                  placeholder="Technology, Retail, Healthcare, etc."
                  data-testid="wizard-partner-industry"
                />
              </div>
            </div>
          )}

          {currentStep === 3 && (
            <div className="text-center py-4" data-testid="wizard-step-generate">
              <div className="w-16 h-16 bg-primary-100 rounded-2xl mx-auto flex items-center justify-center mb-4">
                <Sparkles className="w-8 h-8 text-primary" />
              </div>
              <h2 className="font-heading text-xl font-bold text-stone-900 mb-3">
                Ready to create your first proposal?
              </h2>
              <p className="text-stone-600 text-sm leading-relaxed max-w-sm mx-auto mb-6">
                Head to the AI Generator to create customized proposals, email campaigns, 
                and more using your organization's info.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  onClick={() => {
                    handleFinish();
                    navigate('/ai-generator');
                  }}
                  className="bg-primary hover:bg-primary-800 text-white"
                  data-testid="wizard-go-ai-generator"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  Open AI Generator
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    handleFinish();
                    navigate('/partners');
                  }}
                  className="border-stone-300"
                  data-testid="wizard-go-partners"
                >
                  <Users className="w-4 h-4 mr-2" />
                  View Partners
                </Button>
              </div>
            </div>
          )}

          {currentStep === 4 && (
            <div className="text-center py-4" data-testid="wizard-step-complete">
              <div className="w-16 h-16 bg-green-100 rounded-2xl mx-auto flex items-center justify-center mb-4">
                <Check className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="font-heading text-xl font-bold text-stone-900 mb-3">
                You're all set!
              </h2>
              <p className="text-stone-600 text-sm leading-relaxed max-w-sm mx-auto mb-6">
                Your organization is set up and ready to go. Explore RaiseRoot's tools to 
                supercharge your fundraising.
              </p>
              <div className="grid grid-cols-2 gap-3 max-w-sm mx-auto">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => { handleFinish(); navigate('/ai-generator'); }}
                  className="flex flex-col items-center gap-1 h-auto py-3"
                >
                  <Sparkles className="w-5 h-5 text-primary" />
                  <span className="text-xs">AI Generator</span>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => { handleFinish(); navigate('/design-studio'); }}
                  className="flex flex-col items-center gap-1 h-auto py-3"
                >
                  <Palette className="w-5 h-5 text-primary" />
                  <span className="text-xs">Design Studio</span>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => { handleFinish(); navigate('/grants'); }}
                  className="flex flex-col items-center gap-1 h-auto py-3"
                >
                  <Target className="w-5 h-5 text-primary" />
                  <span className="text-xs">Grants Hub</span>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => { handleFinish(); navigate('/partners'); }}
                  className="flex flex-col items-center gap-1 h-auto py-3"
                >
                  <Users className="w-5 h-5 text-primary" />
                  <span className="text-xs">Partners</span>
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 pb-5 flex items-center justify-between">
          {currentStep > 0 && currentStep < 4 ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleBack}
              className="text-stone-500 hover:text-stone-700"
              data-testid="wizard-back-btn"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Back
            </Button>
          ) : (
            <div />
          )}

          {currentStep < 3 ? (
            <Button
              onClick={handleNext}
              disabled={saving}
              className="bg-primary hover:bg-primary-800 text-white"
              data-testid="wizard-next-btn"
            >
              {saving ? 'Saving...' : currentStep === 0 ? "Let's Go" : 'Next'}
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : currentStep === 4 ? (
            <Button
              onClick={() => { handleFinish(); navigate('/dashboard'); }}
              className="bg-primary hover:bg-primary-800 text-white"
              data-testid="wizard-finish-btn"
            >
              Go to Dashboard
              <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          ) : null}
        </div>

        {/* Step dots */}
        <div className="flex items-center justify-center gap-2 pb-4">
          {STEPS.map((_, i) => (
            <div
              key={i}
              className={`w-2 h-2 rounded-full transition-all ${
                i === currentStep ? 'bg-primary w-6' : i < currentStep ? 'bg-primary/40' : 'bg-stone-200'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
